import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Pagination,
  SearchInput,
} from "@hid-galaxy-ui/galaxy-react";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Snackbar,
} from "@mui/material";
import ValidationStatusTable from "./table/ValidationStatusTable";
import type { ValidationStatusRow } from "./table/ValidationStatusTable";
import "./ValidationStatus.scss";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import {
  deleteValidationStatusSagaAction,
  getValidationPredefinedDataSagaAction,
  getValidationStatusListSagaAction,
} from "../../../../redux/saga/validation-status/saga-actions";
import ValidationStatusFormModal from "./form-modal/ValidationStatusFormModal";

type StatusFilter = "all" | "true" | "false";

const ValidationStatus: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [filterAnchorEl, setFilterAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [filteredRows, setFilteredRows] = React.useState<ValidationStatusRow[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(20);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [selectedRow, setSelectedRow] = React.useState<ValidationStatusRow | null>(
    null
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = React.useState<ValidationStatusRow | null>(
    null
  );
  const [toast, setToast] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const dispatch = useAppDispatch();
  const { list } = useAppSelector((state) => state.validation_status);

  const filterOpen = Boolean(filterAnchorEl);

  React.useEffect(() => {
    dispatch(getValidationStatusListSagaAction());
    dispatch(getValidationPredefinedDataSagaAction());
  }, [dispatch]);

  const onExport = () => {
    const header = ["Partner List", "Technology", "Integration", "Validation Status"];
    const rows = filteredRows.map((r) => [
      r.partner_name,
      r.technology,
      r.integration,
      r.validation_status === 1 ? "TRUE" : "False",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Validation Status");

    const arrayBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileName = `validation-status-${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    saveAs(
      new Blob([arrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName
    );
  };

  const handleAddPartnerClick = () => {
    setSelectedRow(null);
    setModalOpen(true);
  };

  const handleEdit = (row: ValidationStatusRow) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleDelete = (row: ValidationStatusRow) => {
    setDeleteTarget(row);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    dispatch(
      deleteValidationStatusSagaAction({
        ID: deleteTarget.ID,
        onSuccess: () => {
          setToast({
            open: true,
            message: "Partner deleted successfully.",
            severity: "success",
          });
          setConfirmDeleteOpen(false);
          setDeleteTarget(null);
        },
        onError: (message) => {
          setToast({ open: true, message, severity: "error" });
          setConfirmDeleteOpen(false);
          setDeleteTarget(null);
        },
      })
    );
  };

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber || 1);
  };

  const onPageSizesClick = (_e: any, value: any) => {
    setRowsPerPage(Number(value));
    setCurrentPageNumber(1);
  };

  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
    if (currentPageNumber > maxPage) {
      setCurrentPageNumber(maxPage);
    }
  }, [filteredRows.length, rowsPerPage, currentPageNumber]);

  return (
    <div className="validation-status-container">
      <div className="page-title">Validation Status</div>
      <div className="top-actions">
        <Button
          variant="primary"
          label="Create New Partner"
          icon={<Iconography icon="plus" size={IconographySizes.Small} />}
          className="create-partner-button"
          onClick={handleAddPartnerClick}
        />
      </div>
      <div className="search-table-container">
        <div className="search-input-filter-wrapper">
          <div className="left">
            <SearchInput
              theme="dark"
              id="searchValidationStatus"
              name="searchValidationStatus"
              placeholder="Search by Partner / Technology / Integration"
              isDisplayClearIcon={true}
              value={searchTerm}
              onChange={(event: any) => setSearchTerm(event.currentTarget.value)}
              onSearch={(_event, value) => setSearchTerm(value)}
            />
            <Button
              variant={statusFilter === "all" ? "tertiary" : "primary"}
              icon={
                <Iconography icon="sliders" size={IconographySizes.Medium} />
              }
              onClick={(e: any) => setFilterAnchorEl(e.currentTarget)}
            />
            <Menu
              anchorEl={filterAnchorEl}
              open={filterOpen}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem
                selected={statusFilter === "all"}
                onClick={() => {
                  setStatusFilter("all");
                  setFilterAnchorEl(null);
                }}
              >
                All
              </MenuItem>
              <MenuItem
                selected={statusFilter === "true"}
                onClick={() => {
                  setStatusFilter("true");
                  setFilterAnchorEl(null);
                }}
              >
                Validation Status: TRUE
              </MenuItem>
              <MenuItem
                selected={statusFilter === "false"}
                onClick={() => {
                  setStatusFilter("false");
                  setFilterAnchorEl(null);
                }}
              >
                Validation Status: False
              </MenuItem>
            </Menu>
          </div>
          <div className="right">
            <Button
              variant="secondary"
              label="Export"
              icon={<Iconography icon="arrowUpFromSquare" />}
              disabled={filteredRows.length === 0}
              onClick={onExport}
            />
          </div>
        </div>
        <ValidationStatusTable
          rows={list}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onFilteredRowsChange={setFilteredRows}
          currentPageNumber={currentPageNumber}
          rowsPerPage={rowsPerPage}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
        <Pagination
          currentPageNumber={currentPageNumber}
          totalRecords={filteredRows.length}
          pageSize={rowsPerPage}
          border={true}
          onPageNumberClick={handlePageChange}
          onPageSizesClick={onPageSizesClick}
          pageSizes={[
            { label: "10", value: "10" },
            { label: "20", value: "20" },
            { label: "50", value: "50" },
            { label: "100", value: "100" },
          ]}
        />
      </div>
      {modalOpen && (
        <ValidationStatusFormModal
          setModalOpen={setModalOpen}
          mode={selectedRow ? "Update" : "Add"}
          row={selectedRow}
          onActionSuccess={(message) =>
            setToast({ open: true, message, severity: "success" })
          }
          onActionError={(message) =>
            setToast({ open: true, message, severity: "error" })
          }
        />
      )}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Partner</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this partner validation record?
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            label="Cancel"
            onClick={() => {
              setConfirmDeleteOpen(false);
              setDeleteTarget(null);
            }}
          />
          <Button variant="warning" label="Delete" onClick={confirmDelete} />
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ValidationStatus;

